import type { ReactNode } from 'react';
import { Accessibility, Globe2, HeartHandshake, UserCog } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { HrBarList } from '../components/BarList';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  COUNTRY_OF_BIRTH_OPTIONS,
  DISABILITY_STATUS_OPTIONS,
  GENDER_OPTIONS,
  INDIGENOUS_STATUS_OPTIONS,
} from '../../../constants/demographics.constants';
import { countBy, percent } from '../hr-dashboard-utils';
import type { HrPerson } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

type DemographicField =
  | 'gender'
  | 'indigenousStatus'
  | 'countryOfBirth'
  | 'disabilityStatus';

interface Breakdown {
  items: { key: string; label: string; value: number }[];
  recorded: number;
  total: number;
  tooFew: boolean;
}

/**
 * Groups under five are suppressed so no individual can be identified (brief §3 R5).
 * A suppressed category stays visible so the reader knows it exists, but its count and
 * bar are withheld.
 */
const breakdownFor = (
  people: HrPerson[],
  field: DemographicField,
  options: string[]
): Breakdown => {
  const recordedPeople = people.filter((person) => Boolean(person[field]));
  const counts = countBy(recordedPeople, (person) => person[field]);

  return {
    items: options.flatMap((option) => {
      const count = counts.get(option) || 0;
      if (count === 0) return [];
      return [{ key: option, label: option, value: count < 5 ? 0 : count }];
    }),
    recorded: recordedPeople.length,
    total: people.length,
    tooFew: recordedPeople.length > 0 && recordedPeople.length < 5,
  };
};

const BreakdownCard = ({
  title,
  description,
  breakdown,
  icon,
}: {
  title: string;
  description: string;
  breakdown: Breakdown;
  icon: ReactNode;
}) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-primary text-primary-foreground">
          {icon}
        </span>
        <div>
          <CardTitle className="text-sm">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {breakdown.recorded === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">No responses recorded yet.</p>
      ) : breakdown.tooFew ? (
        <p className="py-6 text-sm text-muted-foreground">
          Too few recorded responses to report (fewer than five).
        </p>
      ) : (
        <>
          <HrBarList
            items={breakdown.items}
            formatter={(value) => (value === 0 ? 'Suppressed' : String(value))}
          />
          <p className="mt-3 text-xs text-muted-foreground">
            {breakdown.recorded} of {breakdown.total} people have a recorded response.
          </p>
        </>
      )}
    </CardContent>
  </Card>
);

export function DiversitySection({ people, data }: HrSectionProps) {
  const gender = breakdownFor(people, 'gender', GENDER_OPTIONS);
  const indigenous = breakdownFor(people, 'indigenousStatus', INDIGENOUS_STATUS_OPTIONS);
  const birth = breakdownFor(people, 'countryOfBirth', COUNTRY_OF_BIRTH_OPTIONS);
  const disability = breakdownFor(people, 'disabilityStatus', DISABILITY_STATUS_OPTIONS);

  if (!data.diversity.available) {
    return (
      <div className="space-y-4">
        <Card className="border-dashed">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  Demographic fields are ready — no responses recorded yet
                </CardTitle>
                <CardDescription>
                  The People profile now has Gender, Aboriginal and/or Torres Strait Islander,
                  Country of Birth, Disability Status and Work Location fields. Complete them
                  manually under People → Employment & Personal; this page populates
                  automatically. Null means “not recorded”; “None” is a valid response and is not
                  treated as missing.
                </CardDescription>
              </div>
              <HrStatusPill tone="warning">Awaiting data</HrStatusPill>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { title: 'Gender', icon: <UserCog className="h-5 w-5" /> },
            {
              title: 'Aboriginal & Torres Strait Islander',
              icon: <HeartHandshake className="h-5 w-5" />,
            },
            { title: 'Country of Birth', icon: <Globe2 className="h-5 w-5" /> },
            { title: 'Disability Status', icon: <Accessibility className="h-5 w-5" /> },
          ].map((preview) => (
            <Card key={preview.title}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-primary text-primary-foreground">
                    {preview.icon}
                  </span>
                  <CardTitle className="text-sm">{preview.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex h-5 overflow-hidden rounded-md bg-muted">
                  <div className="w-[62%] bg-muted-foreground opacity-20" />
                  <div className="w-[30%] bg-muted-foreground opacity-10" />
                  <div className="w-[8%] bg-muted-foreground opacity-5" />
                </div>
                <HrStatusPill tone="muted">Awaiting data · layout preview</HrStatusPill>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">How to collect it</CardTitle>
            <CardDescription>
              Recommendation for HR · this dashboard never changes Idarah
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
              <li>
                Open the person under People, choose Edit, and complete the fields under
                Employment & Personal.
              </li>
              <li>
                Treat the fields as voluntary and self-identified; explain the purpose and who can
                access the data before collecting it.
              </li>
              <li>
                Report in aggregate only, suppressing any group under five people so no individual
                can be identified.
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Gender recorded',
            value: percent(gender.recorded, people.length),
            unit: '%',
            detail: `${gender.recorded} of ${people.length} people`,
            icon: <UserCog className="h-5 w-5" />,
          },
          {
            label: 'First Nations recorded',
            value: percent(indigenous.recorded, people.length),
            unit: '%',
            detail: `${indigenous.recorded} of ${people.length} people`,
            icon: <HeartHandshake className="h-5 w-5" />,
          },
          {
            label: 'Country of birth recorded',
            value: percent(birth.recorded, people.length),
            unit: '%',
            detail: `${birth.recorded} of ${people.length} people`,
            icon: <Globe2 className="h-5 w-5" />,
          },
          {
            label: 'Disability status recorded',
            value: percent(disability.recorded, people.length),
            unit: '%',
            detail: `${disability.recorded} of ${people.length} people`,
            icon: <Accessibility className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BreakdownCard
          title="Gender"
          description="Self-identified · aggregates only"
          breakdown={gender}
          icon={<UserCog className="h-5 w-5" />}
        />
        <BreakdownCard
          title="Aboriginal and/or Torres Strait Islander"
          description="Self-identified · aggregates only"
          breakdown={indigenous}
          icon={<HeartHandshake className="h-5 w-5" />}
        />
        <BreakdownCard
          title="Country of Birth"
          description="Cultural and linguistic diversity indicator"
          breakdown={birth}
          icon={<Globe2 className="h-5 w-5" />}
        />
        <BreakdownCard
          title="Disability Status"
          description="Self-identified · adjustments and no-adjustment responses"
          breakdown={disability}
          icon={<Accessibility className="h-5 w-5" />}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Privacy and coverage</CardTitle>
              <CardDescription>
                Groups under five are suppressed so no individual can be identified. “None” is a
                valid response and is not counted as missing.
              </CardDescription>
            </div>
            <HrStatusPill tone="link">Aggregates only</HrStatusPill>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Responses are captured manually on the People profile under Employment & Personal.
            Coverage grows as HR completes the optional fields; the dashboard never edits people.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
